import { useState } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

import { GuruMeditationIcon } from "@/components/icons/guru-meditation-icon"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface ExplanationData {
  context?: string
  breakdown?: string | any[]
  grammar_points?: string[]
}

interface GuruExplanationLoaderProps {
  id: string
  isLoading: boolean
  explanation?: string | ExplanationData
  isEnglish?: boolean
  error?: string
}

export default function GuruExplanationLoader({
  id,
  isLoading,
  explanation,
  isEnglish = false,
  error,
}: GuruExplanationLoaderProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (error) {
    return (
      <div
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border border-dashed p-3",
          isEnglish
            ? "border-red-300 bg-red-50/50 text-red-400 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300"
            : "border-red-300 bg-red-50/50 text-red-400 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300",
        )}
      >
        <GuruMeditationIcon className="size-5 opacity-50 grayscale" />
        <span className="text-xs font-medium">
          Guru could not generate explanation
        </span>
      </div>
    )
  }

  if (isLoading) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "flex w-full animate-pulse items-center gap-2 rounded-lg border border-dashed p-3",
                isEnglish
                  ? "border-blue-300 bg-blue-50/50 text-blue-400 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                  : "border-slate-300 bg-slate-50/50 text-slate-400 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-500",
              )}
            >
              <GuruMeditationIcon className="size-5" />
              <span className="text-xs font-medium">
                Guru generating explanation...
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Guru is meditating...</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  if (!explanation) return null

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-lg border transition-all duration-200",
        isEnglish
          ? "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30"
          : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50",
      )}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-black/5 dark:hover:bg-white/5",
          isEnglish
            ? "text-blue-700 dark:text-blue-200"
            : "text-slate-700 dark:text-slate-200",
        )}
      >
        <div className="flex items-center gap-2">
          <GuruMeditationIcon className="size-5" />
          <span className="text-sm font-medium">
            Guru explanation ready — click to expand
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="size-4 opacity-50" />
        ) : (
          <ChevronDown className="size-4 opacity-50" />
        )}
      </button>

      {isExpanded && (
        <div
          className={cn(
            "border-t px-4 py-3 text-sm leading-relaxed",
            isEnglish
              ? "border-blue-200 text-blue-800 dark:border-blue-800 dark:text-blue-100"
              : "border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-300",
          )}
        >
          {typeof explanation === "string" ? (
            <p>{explanation}</p>
          ) : (
            <div className="flex flex-col gap-4">
              {explanation.context && (
                <div>
                  <h4 className="mb-1 text-xs font-semibold tracking-wider uppercase opacity-70">
                    Context
                  </h4>
                  <p>{explanation.context}</p>
                </div>
              )}
              {explanation.breakdown && (
                <div>
                  <h4 className="mb-1 text-xs font-semibold tracking-wider uppercase opacity-70">
                    Breakdown
                  </h4>
                  {Array.isArray(explanation.breakdown) ? (
                    <div className="grid grid-cols-[1fr_1fr_2fr] gap-x-4 gap-y-2 text-sm">
                      <div className="text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
                        Word
                      </div>
                      <div className="text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
                        Romanization
                      </div>
                      <div className="text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
                        Meaning
                      </div>

                      {explanation.breakdown.map((item: any, idx: number) => (
                        <>
                          <div key={`word-${idx}`} className="font-medium">
                            {item.word}
                          </div>
                          <div key={`rom-${idx}`} className="italic opacity-80">
                            {item.romanization}
                          </div>
                          <div key={`mean-${idx}`}>{item.meaning}</div>
                        </>
                      ))}
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">
                      {explanation.breakdown}
                    </p>
                  )}
                </div>
              )}
              {explanation.grammar_points &&
                explanation.grammar_points.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-xs font-semibold tracking-wider uppercase opacity-70">
                      Grammar Points
                    </h4>
                    <ul className="list-disc pl-4">
                      {explanation.grammar_points.map(
                        (point: any, idx: number) => (
                          <li key={idx}>
                            {typeof point === "string"
                              ? point
                              : point.rule + ": " + point.description}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
